import {
  Announcement,
  Timed,
} from 'functions/src/types/firestore/Game/Announcement';
import { DateTimeFormat } from './DateTimeFormat';

export type DateDifference = {
  date1LaterThanDate2: boolean;
  datesAreSame: boolean;
  seconds: number;
  minutes: number;
  hours: number;
  days: number;
  weeks: number;
  months: number;
  years: number;
};

export type UnitDifferences = {
  [key in Intl.RelativeTimeFormatUnit]: number;
};
const YEAR_IN_MS = 365.25 * 24 * 60 * 60 * 1000;
const MONTH_IN_MS = (365.25 / 12) * 24 * 60 * 60 * 1000;
const WEEK_IN_MS = 7 * 24 * 60 * 60 * 1000;
const DAY_IN_MS = 24 * 60 * 60 * 1000;
const HOUR_IN_MS = 60 * 60 * 1000;
const MINUTE_IN__MS = 60 * 1000;
const SECOND_IN_MS = 1000;

export function getTimeBetweenDates(date1: Date, date2: Date): DateDifference {
  const timeUntilDate = Math.abs(Number(date1) - Number(date2));
  const date1LaterThanDate2 = Number(date1) - Number(date2) > 0;
  // eslint-disable-next-line no-unused-vars, prefer-const
  let differenceRemainingMs = timeUntilDate;

  const yearsPassed = Math.floor(differenceRemainingMs / YEAR_IN_MS);
  differenceRemainingMs -= yearsPassed * YEAR_IN_MS;
  const monthsPassed = Math.floor(differenceRemainingMs / MONTH_IN_MS);
  differenceRemainingMs -= monthsPassed * MONTH_IN_MS;
  const weeksPassed = Math.floor(differenceRemainingMs / WEEK_IN_MS);
  differenceRemainingMs -= weeksPassed * WEEK_IN_MS;
  const daysPassed = Math.floor(differenceRemainingMs / DAY_IN_MS);
  differenceRemainingMs -= daysPassed * DAY_IN_MS;
  const hoursPassed = Math.floor(differenceRemainingMs / HOUR_IN_MS);
  differenceRemainingMs -= hoursPassed * HOUR_IN_MS;
  const minutesPassed = Math.floor(differenceRemainingMs / MINUTE_IN__MS);
  differenceRemainingMs -= minutesPassed * MINUTE_IN__MS;
  const secondsPassed = Math.floor(differenceRemainingMs / SECOND_IN_MS);
  differenceRemainingMs -= secondsPassed * SECOND_IN_MS;
  const datesAreSame = timeUntilDate === 0;

  return {
    date1LaterThanDate2: date1LaterThanDate2,
    datesAreSame: datesAreSame,
    seconds: secondsPassed,
    minutes: minutesPassed,
    hours: hoursPassed,
    days: daysPassed,
    weeks: weeksPassed,
    months: monthsPassed,
    years: yearsPassed,
  };
}

function difference(relativeTo: Date, date: Date): UnitDifferences {
  const dateDifference = getTimeBetweenDates(relativeTo, date);
  const coefficient = dateDifference.date1LaterThanDate2 ? -1 : 1; // 1 if date in the past
  return {
    second: (coefficient * dateDifference.seconds) % 60,
    minute: (coefficient * dateDifference.minutes) % 60,
    hour: (coefficient * dateDifference.hours) % 24,
    day: (coefficient * dateDifference.days) % 7,
    week: (coefficient * dateDifference.weeks) % 4,
    month: coefficient * dateDifference.months,
    year: coefficient * dateDifference.years,
  };
}

const UNITS_LARGE_TO_SMALL: Intl.RelativeTimeFormatUnit[] = [
  'year',
  'month',
  'week',
  'day',
  'hour',
  'minute',
  'second',
];

export function formatRelative(
  date: Date,
  relativeTo: Date = new Date(),
  unitsCount = 1,
  options?: Intl.RelativeTimeFormatOptions,
): string {
  const differences = difference(relativeTo, date);
  const unitsFiltered = UNITS_LARGE_TO_SMALL.filter(
    (unit) => differences[`${unit}`] !== 0,
  );
  if (unitsFiltered.length === 0) {
    return 'just now';
  }
  const units = unitsFiltered.slice(
    0,
    Math.min(unitsCount, unitsFiltered.length),
  );

  const rtf = new Intl.RelativeTimeFormat('en', options);
  return units
    .map((unit, i) => {
      let formatted = rtf.format(differences[`${unit}`], unit);
      if (i > 0) {
        formatted = formatted.replace('in ', '');
      }
      if (i < units.length - 1) {
        formatted = formatted.replace(' ago', '');
      }
      return formatted;
    })
    .join(' ');
}

export function getDateLineContent({
  date,
  endDate,
  checkInDate,
  type,
}: Timed<Date> & { type: Announcement['type'] }) {
  const isBeforeCheckIn = checkInDate && Date.now() < checkInDate.getTime(); // TODO: useServerTime
  const isAfterEndDate = endDate && Date.now() > endDate.getTime(); // TODO: useServerTime

  const dateChosen = isBeforeCheckIn
    ? checkInDate
    : isAfterEndDate
    ? endDate
    : date;

  const formatted = formatRelative(dateChosen, undefined, 2);
  const isPast = formatted.includes('ago');

  if (type === 'asset' || type === 'patch') {
    return `Release${isPast ? 'd' : 's'} ${formatted}`;
  }

  if (isBeforeCheckIn) {
    return `Check-in starts ${formatted}`;
  }
  if (isAfterEndDate) {
    return `Ended ${formatted}`;
  }
  return `Start${isPast ? 'ed' : 's'} ${formatted}`;
}

const TIME_FORMAT: Intl.DateTimeFormatOptions = {
  hour: 'numeric',
  minute: 'numeric',
  timeZoneName: 'short',
};

const DATE_FORMAT: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  month: 'numeric',
  year: undefined,
} as const;

export type FormatDateTimeOptions = {
  dateFormat?: Intl.DateTimeFormatOptions;
  timeFormat?: Intl.DateTimeFormatOptions;
  separator?: string;
};

export function formatDateTime(
  date: Date,
  options: FormatDateTimeOptions = {},
): string {
  const DEFAULT_SEPARATOR = ' ';
  options = {
    dateFormat: DATE_FORMAT,
    timeFormat: TIME_FORMAT,
    separator: DEFAULT_SEPARATOR,
    ...options,
  };
  const time = options.timeFormat
    ? DateTimeFormat(options.timeFormat).format(date)
    : '';
  const timeFormatted =
    options.separator === DEFAULT_SEPARATOR
      ? `${options.separator}(${time})`
      : `${options.separator}${time}`;

  return `${DateTimeFormat(options.dateFormat).format(date)}${timeFormatted}`;
}

export function formatDateRange(
  date1: Date,
  date2: Date,
  options: FormatDateTimeOptions = {},
) {
  if (date1.getDate() === date2.getDate()) {
    const DEFAULT_SEPARATOR = ' ';
    options = {
      dateFormat: DATE_FORMAT,
      timeFormat: TIME_FORMAT,
      separator: DEFAULT_SEPARATOR,
      ...options,
    };
    const date1Time = DateTimeFormat({
      ...options.timeFormat,
      timeZoneName: undefined,
    }).format(date1);
    const date2Time = DateTimeFormat(options.timeFormat).format(date2);
    const timeFormatted =
      options.separator === DEFAULT_SEPARATOR
        ? `${options.separator}(${date1Time} - ${date2Time})`
        : `${options.separator}${date1Time} - ${date2Time}`;
    return `${DateTimeFormat(options.dateFormat).format(
      date1,
    )}${timeFormatted}`;
  } else {
    return `${formatDateTime(date1, options)} ${formatDateTime(
      date2,
      options,
    )}`;
  }
}
